// 9fbef606107a605d69c0edbcd8029e5d
/* eslint-disable no-param-reassign */
import React from 'react';
import PropTypes from 'prop-types';
import logger from '@tcp/core/src/utils/loggerInstance';
import { generateSessionId } from '@tcp/core/src/utils/cookie.util';
import { API_CONFIG } from '@tcp/core/src/services/config';
import FallbackErrorComponent from './ErrorFallbackComponent';
import { DEFAULT_CLASS_NAME, LIFECYCLE_METHODS } from './config';

/**
 * Generate error component
 * @param {object} error
 * @param {string} componentName
 */
export const renderErrorComponent = (error, componentName, moduleDetails) => {
  return React.createElement(
    'div',
    {
      className: DEFAULT_CLASS_NAME,
    },
    FallbackErrorComponent.call(this, { errorMessage: error.message, componentName, moduleDetails })
  );
};

export const logError = ({ name, error, errorInfo, extraData, moduleDetails }) => {
  let { componentName } = errorInfo || {};
  if (!componentName) componentName = extraData?.componentName;
  const { sessionCookieKey } = API_CONFIG;
  const { isHomePage, moduleId, moduleName, slotIndex } = moduleDetails || {};
  let errMsg = '';
  if (isHomePage) {
    errMsg = `Error occured in ${moduleName} component and ModuleId - ${moduleId} on slot - ${slotIndex} `;
    const errMessage = `${errMsg} - ${error?.message}`;
    if (error?.message) delete error.message;
    logger.error({
      error: { ...error, message: errMessage },
      extraData: {
        errorInfo,
        [sessionCookieKey]: generateSessionId(true),
      },
      errorTags: [componentName],
    });
    logger.debug(errMsg, error, errorInfo);
  } else {
    errMsg = `Error in component - ${name}`;
    const errMessage = `${errMsg} - ${error?.message}`;
    if (error?.message) delete error.message;
    logger.error({
      error: errMessage,
      extraData: {
        errorMessage: JSON.stringify(error),
        errorInfo,
        [sessionCookieKey]: generateSessionId(true),
      },
      errorTags: [componentName],
    });
    logger.debug(errMsg, error, errorInfo);
  }
  return true;
};

/**
 * Generate error safe client component
 * @param {function} renderComponent generate component render
 * @param {string} componentName
 */
export const renderClientSafeComponent = (renderComponent, componentName) => {
  return class ErrorBoundary extends React.Component {
    static propTypes = {
      moduleId: PropTypes.string,
      moduleName: PropTypes.string,
      isHomePage: PropTypes.bool,
      slotIndex: PropTypes.string,
    };

    static defaultProps = {
      moduleId: '',
      moduleName: '',
      slotIndex: '',
      isHomePage: false,
    };

    constructor(props) {
      super(props);
      this.state = { hasError: false, error: null, moduleDetails: null };
      this.componentName = componentName;
    }

    componentDidMount() {
      const { isHomePage, moduleId, moduleName, slotIndex } = this.props;
      if (isHomePage && moduleId && moduleName && slotIndex) {
        this.setState({ moduleDetails: { isHomePage, moduleId, moduleName, slotIndex } });
      }
    }

    static getDerivedStateFromError(error) {
      return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
      const { moduleDetails } = this.state || {};
      logError({ name: componentName, error, errorInfo, moduleDetails });
    }

    render() {
      return renderComponent(this.props, this.state);
    }
  };
};

/**
 * Generate error safe functional component
 * @param {*} WrappedComponent
 */
export const functionalSafeComponent = (WrappedComponent, name) => {
  const renderComponent = (passedProps, passedState) => {
    try {
      const { hasError, error, moduleDetails } = passedState;
      return hasError ? (
        renderErrorComponent(error, WrappedComponent.name, moduleDetails)
      ) : (
        <WrappedComponent {...passedProps} />
      );
    } catch (err) {
      const { moduleDetails } = passedState;
      logError({
        name,
        error: err,
        extraData: {
          componentName: WrappedComponent.displayName || WrappedComponent.name,
          ...passedProps,
          ...passedState,
        },
      });
      return renderErrorComponent(err, WrappedComponent.name, moduleDetails);
    }
  };
  return renderClientSafeComponent(renderComponent, WrappedComponent.name);
};

export const functionalSafeComponentStyled = (WrappedComponent, name) => {
  const renderComponent = (passedProps, passedState) => {
    try {
      const { hasError, error, moduleDetails } = passedState;
      return hasError ? (
        renderErrorComponent(error, WrappedComponent.name, moduleDetails)
      ) : (
        <WrappedComponent {...passedProps} {...passedState} />
      );
    } catch (err) {
      const { moduleDetails } = passedState;
      logError({
        name,
        error: err,
        extraData: {
          componentName: WrappedComponent.displayName || WrappedComponent.name,
          ...passedProps,
          ...passedState,
        },
      });
      return renderErrorComponent(err, WrappedComponent.name, moduleDetails);
    }
  };
  return renderClientSafeComponent(renderComponent, WrappedComponent.name);
};

/**
 * Create error safe component methods
 * @param {string} methodName
 * @param {*} WrappedComponent
 */
export const wrapMethod = (methodName, WrappedComponent, name = '') => {
  const originalMethod = WrappedComponent.prototype[methodName];
  if (!originalMethod) {
    return;
  }

  // define default state
  WrappedComponent.prototype.state = {
    hasError: false,
    error: null,
    ...WrappedComponent.prototype.state,
  };

  WrappedComponent.prototype[methodName] = function _componentMethod() {
    try {
      if (methodName === 'render') {
        const { hasError, error } = this.state;
        return hasError
          ? renderErrorComponent(error, WrappedComponent.name)
          : // eslint-disable-next-line prefer-rest-params
            originalMethod.apply(this, arguments);
      }
      // eslint-disable-next-line prefer-rest-params
      return originalMethod.apply(this, arguments);
    } catch (err) {
      logError({
        name,
        error: err,
        errorInfo: WrappedComponent.name,
        extraData: {
          componentName: WrappedComponent.displayName || WrappedComponent.name,
        },
      });
      if (methodName === 'render') {
        return renderErrorComponent(err, WrappedComponent.name);
      }
      if (methodName === 'shouldComponentUpdate') {
        return false;
      }
      return false;
    }
  };
};

/**
 * Generate error safe non functional component
 * @param {*} WrappedComponent
 */
export const nonFunctionalSafeComponent = (WrappedComponent, name) => {
  LIFECYCLE_METHODS.forEach((method) => {
    return WrappedComponent.target
      ? wrapMethod(method, WrappedComponent.target, name)
      : wrapMethod(method, WrappedComponent, name);
  });
  const renderComponent = (passedProps) => <WrappedComponent {...passedProps} />;
  return renderClientSafeComponent(renderComponent, name);
};

/**
 * Generate error safe component
 * @param {*} WrappedComponent
 */
const SafeComponent = (WrappedComponent, name = '') => {
  if (WrappedComponent.target) {
    return !(WrappedComponent.target.prototype && WrappedComponent.target.prototype.render)
      ? functionalSafeComponentStyled(WrappedComponent, name)
      : nonFunctionalSafeComponent(WrappedComponent, name);
  }

  return !(WrappedComponent.prototype && WrappedComponent.prototype.render)
    ? functionalSafeComponent(WrappedComponent, name)
    : nonFunctionalSafeComponent(WrappedComponent, name);
};

export default SafeComponent;
